/**
 * C6, one host session per workspace host: its lease (owner, epoch, heartbeat) and the one seq
 * counter every delta it sends takes a number from, so the stage op (ST2) and the sync engine (WS1)
 * share them instead of racing. The deltas are #872's format (C7); `not_kept` is WS2's.
 */
import { batchTransport, leaseApi, SeqConflict, type LeaseApi } from "./api";

export interface HostSession {
  workspaceId: string;
  /** The lease owner: this tab's id, or "hosted:<instance>". */
  owner: string;
  epoch: number;
  /**
   * Sends changes as one batch, numbered when their turn comes: every sender's batches go out one
   * at a time in the order they were sent, so their seqs never leave a gap or arrive out of order.
   * The only way a delta gets a seq.
   */
  send(changes: DeltaChange[]): Promise<{ seq: number }>;
  heartbeat(): Promise<void>;
  /**
   * Sends every change the host made that is not on the server yet, before a release (ER4
   * handover, a hot-cap suspend). The sync engine (sync/engine.ts) supplies it on a session it
   * syncs (`withSync`); a session without one has nothing of its own to send.
   */
  flush(): Promise<void>;
  release(): Promise<void>;
}

export interface DeltaTransport {
  putBatch(epoch: number, ops: DeltaOp[]): Promise<{ seq: number }>;
}

export type DeltaOp =
  | {
      seq: number;
      op: "put";
      path: string;
      sha256: string;
      mtime?: number;
      /** gzip */ body: Uint8Array;
    }
  | { seq: number; op: "delete"; path: string }
  | { seq: number; op: "not_kept"; path: string; size: number };

type WithoutSeq<Op> = Op extends DeltaOp ? Omit<Op, "seq"> : never;

/** A delta before its turn to be numbered. */
export type DeltaChange = WithoutSeq<DeltaOp>;

/** Where a session takes its lease and sends its deltas (tests hand it fakes). */
export interface SessionApi {
  lease: LeaseApi;
  transport(workspaceId: string): DeltaTransport;
}

const restApi: SessionApi = { lease: leaseApi, transport: batchTransport };

/**
 * Acquire the workspace's lease for `owner` and open its session; a lease another host holds is a
 * `LeaseError` (`lease_held`). Seqs go on from the last one the server applied in this epoch. A
 * batch that failed gives its seqs back (batches go one at a time, so nothing was numbered
 * meanwhile), so the next one leaves no gap it would be refused for. A batch the server says is
 * out of order (its answer to an earlier one was lost after it applied) is numbered again from
 * the seq the server expects and sent once more: puts, deletes and not_kept land the same twice.
 */
export async function openHostSession(
  workspaceId: string,
  owner: string,
  api: SessionApi = restApi
): Promise<HostSession> {
  const { epoch, seq } = await api.lease.acquire(workspaceId, owner);
  const transport = api.transport(workspaceId);
  let next = seq + 1;
  let turn: Promise<unknown> = Promise.resolve();

  async function sendNow(changes: DeltaChange[], resynced = false): Promise<{ seq: number }> {
    const first = next;
    const ops = changes.map((change) => ({ ...change, seq: next++ }) as DeltaOp);
    try {
      return await transport.putBatch(epoch, ops);
    } catch (error) {
      next = first;
      if (!(error instanceof SeqConflict) || resynced) throw error;
      next = error.expectedSeq;
      return sendNow(changes, true);
    }
  }

  return {
    workspaceId,
    owner,
    epoch,
    send(changes) {
      const sent = turn.then(() => sendNow(changes));
      turn = sent.catch(() => undefined);
      return sent;
    },
    async heartbeat() {
      await api.lease.heartbeat(workspaceId, owner, epoch);
    },
    flush: async () => undefined,
    release: () => api.lease.release(workspaceId, owner, epoch),
  };
}
